import { useState, useCallback, useContext, useMemo } from "react";
import { toast } from "react-toastify";

import {
  listWabaTemplates,
  syncWabaTemplates,
  deleteWabaTemplate,
} from "../../../services/wabaTemplates.api";
import { WhatsAppsContext } from "../../../context/WhatsApp/WhatsAppsContext";
import toastError from "../../../errors/toastError";

// Estados de plantilla en Meta que exponemos como filtro.
export const STATUS_OPTIONS = [
  { value: "APPROVED", label: "Aprobada" },
  { value: "PENDING", label: "Pendiente" },
  { value: "REJECTED", label: "Rechazada" },
  { value: "PAUSED", label: "Pausada" },
  { value: "DISABLED", label: "Deshabilitada" },
];

export function useWabaTemplatesPage() {
  const { whatsApps } = useContext(WhatsAppsContext);

  // Solo las conexiones WABA (Meta Cloud API) tienen plantillas HSM.
  const wabaConnections = useMemo(
    () => (whatsApps || []).filter((w) => w.provider === "waba"),
    [whatsApps]
  );

  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [syncing, setSyncing] = useState(false);

  const [searchParam, setSearchParam] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [whatsappFilter, setWhatsappFilter] = useState("");

  const fetchTemplates = useCallback(
    async (search = searchParam, status = statusFilter, whatsappId = whatsappFilter) => {
      setLoading(true);
      try {
        const { data } = await listWabaTemplates({
          searchParam: search || undefined,
          status: status || undefined,
          whatsappId: whatsappId || undefined,
        });
        setTemplates(Array.isArray(data) ? data : []);
      } catch (err) {
        toastError(err);
      } finally {
        setLoading(false);
      }
    },
    [searchParam, statusFilter, whatsappFilter]
  );

  const handleSearch = (value) => {
    setSearchParam(value);
    fetchTemplates(value, statusFilter, whatsappFilter);
  };

  const handleStatusFilter = (value) => {
    setStatusFilter(value);
    fetchTemplates(searchParam, value, whatsappFilter);
  };

  const handleWhatsappFilter = (value) => {
    setWhatsappFilter(value);
    fetchTemplates(searchParam, statusFilter, value);
  };

  // Sincroniza desde Meta la conexión indicada por el filtro. Sin conexión elegida
  // no se puede sincronizar (Meta requiere un WABA concreto).
  const handleSync = async () => {
    const targetId =
      whatsappFilter || (wabaConnections.length === 1 ? wabaConnections[0].id : "");
    if (!targetId) {
      toast.info("Selecciona una conexión WABA para sincronizar sus plantillas.");
      return;
    }
    setSyncing(true);
    try {
      const { data } = await syncWabaTemplates(targetId);
      const processed = data?.processed ?? data?.count ?? 0;
      toast.success(`Sincronización completa: ${processed} plantilla(s).`);
      fetchTemplates(searchParam, statusFilter, whatsappFilter);
    } catch (err) {
      toastError(err);
    } finally {
      setSyncing(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      await deleteWabaTemplate(id);
      toast.success("Plantilla eliminada.");
      fetchTemplates(searchParam, statusFilter, whatsappFilter);
    } catch (err) {
      toastError(err);
    }
  };

  return {
    templates,
    loading,
    syncing,
    searchParam,
    statusFilter,
    whatsappFilter,
    wabaConnections,
    STATUS_OPTIONS,
    fetchTemplates,
    handleSearch,
    handleStatusFilter,
    handleWhatsappFilter,
    handleSync,
    handleDelete,
  };
}
